import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { Product, CartItem } from '../types';
import { normalizeProduct } from '../utils/helpers';

interface CartContextType {
    items: CartItem[];
    totalItems: number;
    totalAmount: number;
    addToCart: (product: Product, quantity?: number) => void;
    removeFromCart: (productId: number) => void;
    updateQuantity: (productId: number, quantity: number) => void;
    clearCart: () => void;
    getCartItem: (productId: number) => CartItem | undefined;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

interface CartProviderProps {
    children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
    const [items, setItems] = useState<CartItem[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);

    // Load cart from localStorage on mount
    useEffect(() => {
        const savedCart = localStorage.getItem('eldai_cart');
        console.log('Loading cart from localStorage:', savedCart);
        if (savedCart) {
            try {
                const parsedCart = JSON.parse(savedCart);
                console.log('Parsed cart:', parsedCart);
                // Normalize products when loading from localStorage
                const normalizedCart = parsedCart.map((item: CartItem) => ({
                    ...item,
                    product: item.product ? normalizeProduct(item.product) : null
                }));
                console.log('Normalized cart:', normalizedCart);
                setItems(normalizedCart);
            } catch (error) {
                console.error('Error loading cart from localStorage:', error);
            }
        }
        setIsLoaded(true);
    }, []);

    // Save cart to localStorage whenever items change, but only after initial load
    useEffect(() => {
        if (!isLoaded) return; // Don't save during initial load
        
        console.log('Saving cart to localStorage:', items);
        localStorage.setItem('eldai_cart', JSON.stringify(items));
    }, [items, isLoaded]);

    // Calculate totals
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = items.reduce((sum, item) => {
        const unitPrice = typeof item.product?.unit_price === 'string'
            ? parseFloat(item.product.unit_price)
            : item.product?.unit_price || 0;
        const pcb = item.product?.pcb || 1;
        const packPrice = unitPrice * pcb;
        return sum + (packPrice * item.quantity);
    }, 0);

    const addToCart = useCallback((product: Product, quantity: number = 1) => {
        const normalizedProduct = normalizeProduct(product);
        setItems(currentItems => {
            const existingItem = currentItems.find(item => item.product_id === normalizedProduct.id);

            if (existingItem) {
                // Update quantity of existing item
                return currentItems.map(item =>
                    item.product_id === normalizedProduct.id
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            } else {
                // Add new item
                const newItem: CartItem = {
                    id: Date.now(), // Simple ID generation
                    product_id: normalizedProduct.id,
                    quantity,
                    product: normalizedProduct
                };
                return [...currentItems, newItem];
            }
        });
    }, []);

    const removeFromCart = useCallback((productId: number) => {
        setItems(currentItems =>
            currentItems.filter(item => item.product_id !== productId)
        );
    }, []);

    const updateQuantity = useCallback((productId: number, quantity: number) => {
        if (quantity <= 0) {
            removeFromCart(productId);
            return;
        }

        setItems(currentItems =>
            currentItems.map(item =>
                item.product_id === productId
                    ? { ...item, quantity }
                    : item
            )
        );
    }, [removeFromCart]);

    const clearCart = useCallback(() => {
        setItems([]);
    }, []);

    const getCartItem = useCallback((productId: number): CartItem | undefined => {
        return items.find(item => item.product_id === productId);
    }, [items]);

    const value: CartContextType = useMemo(() => ({
        items,
        totalItems,
        totalAmount,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartItem
    }), [items, totalItems, totalAmount, addToCart, removeFromCart, updateQuantity, clearCart, getCartItem]);

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};
